import type { JSX } from 'react';
import { allItems, allSets, type Item } from '@bratva/content';
import type { GearSlotKey, SaveDoc } from '@bratva/state';
import { setCollected } from '@bratva/rules';
import { character } from '../game/character.js';
import { store } from '../game/store.js';

interface Props {
  save: SaveDoc;
  onClose(): void;
}

const SLOT_TITLES: Record<GearSlotKey, string> = {
  kostyum: 'Костюм',
  obuv: 'Обувь',
  cep: 'Цепь',
  persten: 'Перстни',
  barsetka: 'Барсетка',
  voda: 'Вода',
  partak: 'Партак',
};

const rub = (kop: number): string => `${(kop / 100).toLocaleString('ru-RU')} ₽`;

/** Барыга на рынке: купить, надеть, посмотреть, что даёт сет. */
export function ShopPanel({ save, onClose }: Props): JSX.Element {
  const { worn, gear } = character(save);
  const items = allItems();

  const buy = (item: Item): void =>
    store.dispatch({ type: 'buyItem', def: item.def, title: item.title, price: item.price, minutes: 5 });

  const equip = (item: Item): void =>
    store.dispatch({ type: 'equip', slot: item.slot, def: item.def, title: item.title });

  const unequip = (item: Item): void =>
    store.dispatch({ type: 'equip', slot: item.slot, def: null, title: item.title });

  return (
    <div className="shop">
      <div className="deeds-head">
        <b>Барыга</b>
        <button type="button" className="deeds-close" onClick={onClose} aria-label="Закрыть">
          ✕
        </button>
      </div>

      <div className="shop-list">
        {items.map((item) => {
          const owned = save.owned.includes(item.def);
          const wornNow = save.equipment[item.slot] === item.def;
          const affordable = save.player.cash >= item.price;

          return (
            <div key={item.def} className={wornNow ? 'shop-item worn' : 'shop-item'}>
              <div className="shop-item-top">
                <span className="shop-item-title">{item.title}</span>
                <span className="shop-item-price">{owned ? 'своё' : rub(item.price)}</span>
              </div>
              <span className="shop-item-meta">
                {SLOT_TITLES[item.slot]}
                {item.stats
                  ? ` · ${Object.entries(item.stats)
                      .map(([stat, value]) => `${stat} ${value! > 0 ? '+' : ''}${value}`)
                      .join(', ')}`
                  : ''}
                {item.quality ? ` · палёнка ${Math.round(item.quality * 100)}%` : ''}
              </span>
              {item.hint && <span className="shop-item-hint">{item.hint}</span>}

              <div className="shop-item-actions">
                {!owned && (
                  <button type="button" disabled={!affordable} onClick={() => buy(item)}>
                    {affordable ? 'Купить' : 'Не хватает'}
                  </button>
                )}
                {owned && !wornNow && (
                  <button type="button" onClick={() => equip(item)}>
                    Надеть
                  </button>
                )}
                {wornNow && (
                  <button type="button" onClick={() => unequip(item)}>
                    Снять
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="shop-sets">
        <span className="hud-caption">Сеты</span>
        {allSets().map((set) => (
          <p key={set.def} className={setCollected(set, worn) ? 'set on' : 'set'}>
            <b>{set.title}</b> <span>{set.effect}</span>
          </p>
        ))}
        {gear.shameChance > 0 && (
          <p className="deeds-blocked">Палёнка на виду: {Math.round(gear.shameChance * 100)}% опозориться.</p>
        )}
      </div>
    </div>
  );
}
